import { TURNSTILE_HEADER } from "@/lib/turnstile";

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://api.manticscore.com";

const DEVICE_STORAGE_KEY = "manticscore.web-capture.device.v1";
const TOKEN_STORAGE_KEY = "manticscore.web-capture.token.v1";
const CAPTURE_STORAGE_KEY = "manticscore.web-capture.latest.v1";
const TOKEN_EXPIRY_BUFFER_MS = 30_000;

export type CapturePhase =
  | "idle"
  | "submitting"
  | "processing"
  | "captured"
  | "publishing"
  | "ready"
  | "error";

export type SessionLink = {
  id: string;
  url_canonical: string;
  source_kind: string;
  status: string;
  title: string | null;
  summary: string | null;
  image_url?: string | null;
  error_detail?: unknown;
  rejection_reason?: string | null;
};

export type IntentSession = {
  id: string;
  title: string | null;
  intent_summary: string | null;
  clip_slug: string | null;
  clip_published: boolean;
  display_link_count: number;
};

export type SessionDetail = {
  session: IntentSession;
  links: SessionLink[];
  edges: unknown[];
  last_event_seq: number;
};

/**
 * backend#354, additive on a successful anonymous `POST /v1/shares`.
 * Signed-in responses never carry this; its mere presence is the entire
 * "show the CTA" signal — never infer this from a counter kept client-side.
 *
 * `code` is intentionally typed as `string`: new codes will ship on the
 * backend before this file changes, and an unrecognized one must still
 * render the generic copy below rather than fail to render.
 */
export type SignupPrompt = {
  code: string;
  links_used_today?: number;
  links_remaining_today?: number;
  daily_limit?: number;
};

export type CaptureResult = {
  link_id: string;
  session_id: string;
  session_title: string | null;
  session_created: boolean;
  signup_prompt?: SignupPrompt | null;
};

export type PublishedClip = {
  slug: string;
  clip_url: string;
  published_at: string;
};

export type SavedCapture = {
  sessionId: string;
  clipUrl: string;
  slug: string;
  updatedAt: number;
};

type StoredToken = {
  value: string;
  expiresAt: number;
};

type ApiErrorPayload = {
  detail?: string | { code?: string; reason?: string };
};

export class AnonymousCaptureError extends Error {
  status: number;
  code: string | null;

  constructor(message: string, status = 0, code: string | null = null) {
    super(message);
    this.name = "AnonymousCaptureError";
    this.status = status;
    this.code = code;
  }
}

function safeGet(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Private browsing and hardened storage policies can reject writes.
  }
}

function safeRemove(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Treat storage as an optional enhancement.
  }
}

function parseStored<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function randomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function normalizeCaptureUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const value = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const url = new URL(value);
    if (!url.hostname.includes(".")) return null;
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    if (url.username || url.password) return null;
    return url.toString();
  } catch {
    return null;
  }
}

/** Existing device id, or null. Does not mint one. */
export function peekAnonymousDeviceId(): string | null {
  return safeGet(window.localStorage, DEVICE_STORAGE_KEY);
}

export function getAnonymousDeviceId(): string {
  const existing = peekAnonymousDeviceId();
  if (existing) return existing;

  const created = `web-${randomId()}`;
  safeSet(window.localStorage, DEVICE_STORAGE_KEY, created);
  return created;
}

function readToken(): StoredToken | null {
  const stored = parseStored<StoredToken>(safeGet(window.sessionStorage, TOKEN_STORAGE_KEY));
  if (!stored?.value || stored.expiresAt <= Date.now() + TOKEN_EXPIRY_BUFFER_MS) {
    safeRemove(window.sessionStorage, TOKEN_STORAGE_KEY);
    return null;
  }
  return stored;
}

/**
 * Proof-of-human challenge token (BK-153-1). Absent when Turnstile is
 * unavailable — see src/lib/turnstile.ts for the fail-open rationale and
 * HANDOFF-backend-turnstile-contract.md for the wire contract.
 *
 * SINGLE-USE, AND THE BACKEND ENFORCES IT. `services/challenge_tier.py`
 * claims the sha256 of each response token in Redis with `SET NX` *before*
 * it is sent to Cloudflare, so the second request carrying the same token is
 * refused with 403 `challenge_failed` (reason `token_replayed`) — a replay is
 * rejected by us rather than relying on siteverify being idempotent.
 *
 * The token therefore has to ride exactly ONE request. `takeChallengeToken`
 * is the only reader and it clears as it reads, so a double-spend is
 * impossible by construction rather than by remembering to be careful.
 */
let _challengeToken: string | null = null;

/**
 * The token most recently attached to a request. Already spent — kept only so
 * that a bare 5xx can still be recognised as challenge-related after the fact
 * (see `isChallengeRecoverable`), which is the one thing clearing the token
 * eagerly would otherwise break.
 */
let _spentChallengeToken: string | null = null;

/** Supplies the next challenge token. Passing null clears it (single-use). */
export function setChallengeToken(token: string | null): void {
  _challengeToken = token;
  // A fresh challenge supersedes whatever the last request spent.
  _spentChallengeToken = null;
}

export function getChallengeToken(): string | null {
  return _challengeToken;
}

/**
 * Reads the challenge token and clears it in the same step: it can only ever
 * be spent once, so holding it after it has been attached to a request is a
 * guaranteed `challenge_failed` on the next one.
 */
function takeChallengeToken(): string | null {
  const token = _challengeToken;
  if (token) {
    _challengeToken = null;
    _spentChallengeToken = token;
  }
  return token;
}

/** True when a challenge is held or was attached to the request that just failed. */
function challengeWasSent(): boolean {
  return Boolean(_challengeToken || _spentChallengeToken);
}

function challengeHeaders(): Record<string, string> {
  const token = takeChallengeToken();
  return token ? { [TURNSTILE_HEADER]: token } : {};
}

/**
 * Mints the anonymous session token.
 *
 * Deliberately does NOT carry the challenge header. A Turnstile token is
 * single-use, and this call is an internal prerequisite of a capture rather
 * than the operation the challenge buys: on the backend the tier resolved
 * here feeds telemetry only (`routes/sessions.py` puts it in a PostHog
 * property), while the tier resolved on `POST /v1/shares` is what sizes the
 * per-principal link budget (`services/abuse_counters.py`). Spending the
 * token here would burn it for a log line and leave the capture itself to be
 * refused as a replay.
 */
async function mintAnonymousToken(signal?: AbortSignal): Promise<string> {
  const deviceId = getAnonymousDeviceId();
  const response = await fetch(`${API_BASE_URL}/auth/anonymous`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Device-ID": deviceId,
    },
    body: JSON.stringify({ device_id: deviceId }),
    signal,
  });

  if (!response.ok) throw await responseError(response);

  const body = (await response.json()) as { token: string; expires_in: number };
  const token = {
    value: body.token,
    expiresAt: Date.now() + body.expires_in * 1000,
  };
  safeSet(window.sessionStorage, TOKEN_STORAGE_KEY, JSON.stringify(token));
  return token.value;
}

async function getAnonymousToken(signal?: AbortSignal): Promise<string> {
  return readToken()?.value ?? mintAnonymousToken(signal);
}

async function responseError(response: Response): Promise<AnonymousCaptureError> {
  let payload: ApiErrorPayload = {};
  try {
    payload = (await response.json()) as ApiErrorPayload;
  } catch {
    // A generic message is clearer than leaking an upstream HTML error page.
  }

  const detail = payload.detail;
  const code = typeof detail === "object" && detail ? detail.code ?? null : null;
  const reason = typeof detail === "object" && detail ? detail.reason : undefined;
  let message = "ManticsCore could not process that link. Please try again.";

  if (code === "invalid_url") message = "Paste a complete public URL and try again.";
  if (code === "rejected") message = reason ? `That URL cannot be processed: ${reason}.` : "That URL cannot be processed.";
  if (code === "duplicate_in_session") message = "That source is already in this thread.";
  if (code === "anonymous_link_limit") message = "This browser has reached its 3-link limit for today.";
  if (code === "no_ready_links") message = "The source is still being understood. Try publishing again in a moment.";
  if (response.status === 429 && code !== "anonymous_link_limit") message = "This browser has reached its free daily limit. Try again after the limit resets.";
  if (response.status >= 500) message = "The service is having trouble right now. Please try again shortly.";
  // Challenge codes are checked last: a challenge_unavailable arrives as a 5xx
  // and its specific message must win over the generic one.
  if (code === "challenge_required") message = "Please complete the human check and try again.";
  if (code === "challenge_failed") message = "That human check could not be verified. Try the check again.";
  if (code === "challenge_unavailable") message = "The human check is temporarily unavailable. Please try again in a moment.";

  return new AnonymousCaptureError(message, response.status, code);
}

/**
 * `challenge` opts this request into carrying the single-use Turnstile header.
 * Off by default so the token is never spent by a background poll: only the
 * caller that knows the token buys something (the share) asks for it.
 */
async function anonymousRequest<T>(
  path: string,
  init: RequestInit = {},
  retryAuth = true,
  challenge = false,
): Promise<T> {
  const token = await getAnonymousToken(init.signal ?? undefined);
  const deviceId = getAnonymousDeviceId();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "X-Device-ID": deviceId,
      Authorization: `Bearer ${token}`,
      ...(challenge ? challengeHeaders() : {}),
      ...init.headers,
    },
  });

  if (response.status === 401 && retryAuth) {
    safeRemove(window.sessionStorage, TOKEN_STORAGE_KEY);
    // The challenge (if any) was spent on the attempt above, so the retry goes
    // out unverified rather than replaying a burned token. A 401 is resolved
    // before the handler runs, so nothing was actually consumed server-side;
    // the cost is one capture landing on the stricter tier, not a refusal.
    return anonymousRequest<T>(path, init, false, challenge);
  }
  if (!response.ok) throw await responseError(response);
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function captureUrl(
  url: string,
  sessionId: string | null,
  signal?: AbortSignal,
): Promise<CaptureResult> {
  return anonymousRequest<CaptureResult>(
    "/v1/shares",
    {
      method: "POST",
      body: JSON.stringify({
        url,
        idempotency_key: randomId(),
        ...(sessionId ? { session_id: sessionId } : { new_session: true }),
      }),
      signal,
    },
    /* retryAuth */ true,
    // The one request that carries the challenge: this is where the backend
    // turns the tier into an actual per-principal link budget.
    /* challenge */ true,
  );
}

export async function getIntentSession(
  sessionId: string,
  signal?: AbortSignal,
): Promise<SessionDetail> {
  return anonymousRequest<SessionDetail>(`/v1/threads/${encodeURIComponent(sessionId)}`, { signal });
}

export async function removeSessionLink(
  sessionId: string,
  linkId: string,
  signal?: AbortSignal,
): Promise<void> {
  return anonymousRequest<void>(
    `/v1/threads/${encodeURIComponent(sessionId)}/links/${encodeURIComponent(linkId)}`,
    { method: "DELETE", signal },
  );
}

export async function publishIntentSession(
  sessionId: string,
  signal?: AbortSignal,
): Promise<PublishedClip> {
  return anonymousRequest<PublishedClip>(
    `/v1/threads/${encodeURIComponent(sessionId)}/publish-clip`,
    { method: "POST", signal },
  );
}

export function loadSavedCapture(): SavedCapture | null {
  const saved = parseStored<SavedCapture>(safeGet(window.localStorage, CAPTURE_STORAGE_KEY));
  if (!saved?.sessionId || !saved.clipUrl || !saved.slug) return null;
  return saved;
}

export function saveCapture(capture: SavedCapture): void {
  safeSet(window.localStorage, CAPTURE_STORAGE_KEY, JSON.stringify(capture));
}

export function clearSavedCapture(): void {
  safeRemove(window.localStorage, CAPTURE_STORAGE_KEY);
}

export function linkIsPublishable(link: SessionLink | undefined): boolean {
  return link?.status === "ready" || link?.status === "partial";
}

export function linkIsTerminalFailure(link: SessionLink | undefined): boolean {
  return link?.status === "failed" || link?.status === "rejected";
}

/** Backend codes that mean the challenge itself is the problem (BK-153-1). */
export const CHALLENGE_ERROR_CODES = [
  "challenge_required",
  "challenge_failed",
  "challenge_unavailable",
] as const;

/**
 * True when a failure means "get a fresh challenge and let the person retry".
 * A used Turnstile token cannot be replayed, so the widget must be reset first.
 *
 * Includes 5xx: if the backend's risk service is down we fail CLOSED on the
 * client (we never treat an unverifiable token as verified) but offer a retry
 * with a fresh token, since the stale one would be rejected anyway.
 *
 * The 5xx arm asks `challengeWasSent()`, not `getChallengeToken()`: by the
 * time a response comes back the token has already been cleared by the
 * request that carried it, so reading the live token here would classify
 * every challenged 5xx as unrelated to the challenge.
 */
export function isChallengeRecoverable(error: unknown): boolean {
  if (!(error instanceof AnonymousCaptureError)) return false;
  if (error.code && (CHALLENGE_ERROR_CODES as readonly string[]).includes(error.code)) return true;
  return error.status >= 500 && challengeWasSent();
}

/**
 * Copy for the #117 signup CTA. `null` means "do not render anything" —
 * the caller must gate purely on the presence of `prompt` (never a
 * client-kept counter), so this only ever runs on a block that already
 * arrived.
 *
 * `links_remaining_today` is optional and unchecked-null-safe (this repo runs
 * with `strictNullChecks: false`): a missing or non-finite value falls back
 * to generic copy rather than rendering `undefined` or `NaN`. Singular vs.
 * plural is handled at exactly 1; 0 (the person just hit today's limit) gets
 * its own copy since it is the highest-intent moment in the funnel, not an
 * error state.
 */
export function signupPromptCopy(prompt: SignupPrompt | null | undefined): string | null {
  if (!prompt) return null;
  const remaining = prompt.links_remaining_today;
  if (typeof remaining !== "number" || !Number.isFinite(remaining)) {
    return "Sign up to keep unlimited threads.";
  }
  if (remaining <= 0) {
    return "You've used today's free links — sign up to keep unlimited threads.";
  }
  const links = remaining === 1 ? "free link" : "free links";
  return `${remaining} ${links} left today — sign up to keep unlimited threads.`;
}
